import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of, switchMap, take } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { loginReturnParams } from 'src/app/shared/utils/session-tabs';
import { CupoRegistroService } from 'src/app/dashboard/services/cupo-registro.service';
import { operaComoAprobado } from 'src/app/shared/utils/estado-compliance.util';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class ActivateGuard implements CanActivate {
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private cognitoService: CognitoService,
    private cupoRegistroService: CupoRegistroService
  ) {}

  isNotNullOrEmpty(object: Object) {
    return object && Object.keys(object).length > 0;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.getFromStoreOrAPI().pipe(
      switchMap(async (res) => {
        const currentUrl = state.url;

        if (currentUrl.includes('/wallet/client/company-registration')) {
          return true;
        }

        if (!res.isLogged) {
          const user = await this.cognitoService.getUser();
          if (!user) {
            return this.loginTree(state.url);
          }
          return true;
        }

        // Aprobado (3) o bloqueado (4): el bloqueado opera igual que un
        // aprobado; solo sus depósitos por cartola no se acreditan solos.
        if (this.isNotNullOrEmpty(res.user) && operaComoAprobado(res.user)) {
          return true;
        }

        // PRE operativo: un usuario aún no aprobado por compliance pero con
        // cupo de registro válido y desbloqueado (`puedeOperar`) accede a
        // las mismas funcionalidades que un aprobado. Fail-closed: si el
        // cupo no se puede leer, `puedeOperar` es false y cae al flujo de
        // "completa tu validación".
        const cupo = await this.cupoRegistroService.ensureMe();
        if (cupo?.puedeOperar === true) {
          return true;
        }

        // Obtener isCompany fresco desde Cognito en cada navegación
        let isCompany = 0;
        try {
          const cognitoUser = await this.cognitoService.getAuthUser();
          isCompany = cognitoUser.attributes['custom:is-company'] === '0' ? 0 : 1;
        } catch (e) {
          // Si no hay sesión Cognito, dejar isCompany = 0
        }

        Swal.fire({
          icon: 'warning',
          title: 'Completa tu validación',
          text: 'Para poder acceder a esta funcionalidad debes completar tu proceso de validación. Si ya está en proceso, por favor espera a que finalice.',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#1e5a3f',
        });

        // Redirige según el tipo de cuenta actual de Cognito
        const redirectRoute =
          isCompany === 1
            ? '/wallet/client/company-registration'
            : '/wallet/home';
        return this.router.createUrlTree([redirectRoute]);
      })
    );
  }

  getFromStoreOrAPI(): Observable<any> {
    return this.store.select('auth').pipe(take(1));
  }

  /** Login con la página pedida anotada, para volver a ella al entrar. */
  private loginTree(url: string): UrlTree {
    return this.router.createUrlTree(['/auth/login'], {
      queryParams: loginReturnParams(url),
    });
  }
}
